// One gate on the gates page: its queue (live, or the peak for a past game) with a queue-time
// sparkline, its entrances and exits (live: rate now and whether it's rising or falling), and its
// share of all entries and exits. Clicking opens the gate's details.
import React from 'react';
import Icon from '../../../components/atoms/Icon';
import { Sparkline, unitFormatter } from '../../Concession/live/LiveComponents';
import GateIcon from './GateIcon';
import { queueColor, minuteToClock, formatRate } from '../gatesMetrics';

// Rising green, falling red (for entrances and exits alike)
const RISING_COLOR = '#16a34a';
const FALLING_COLOR = '#dc2626';

// "↗ Rising 24%" / "↘ Falling 12%" / "— Steady", against the previous 15 minutes
const RateTrend = ({ trend }) => {
  if (!trend) return <span className="gx-rate-trend flat" title="Shows once there are 30 minutes of readings">No trend yet</span>;
  const { direction, pct, now, prev } = trend;
  const label = direction === 'flat' ? 'Steady' : `${direction === 'up' ? 'Rising' : 'Falling'}${pct != null ? ` ${pct}%` : ''}`;
  const icon = direction === 'flat' ? 'dash' : direction === 'up' ? 'arrow-up-right' : 'arrow-down-right';
  return (
    <span
      className={`gx-rate-trend ${direction}`}
      style={direction === 'flat' ? undefined : { color: direction === 'up' ? RISING_COLOR : FALLING_COLOR }}
      title={`Last 15 min: ${formatRate(now)}/min · 15 min before: ${formatRate(prev)}/min`}
    >
      <Icon name={icon} />
      {label}
    </span>
  );
};

const GateCard = ({ stats, isLive, entryShare, exitShare, onOpen, toTime, range, gatesOpen, gameStart }) => {
  const {
    gate, label, hasQueue, queue: queueSeries, queueNow, queuePeak, entrances, exits,
    entryRate, exitRate, entryTrend, exitTrend,
  } = stats;
  const queue = isLive ? queueNow : queuePeak;
  const color = queue ? queueColor(queue.value) : 'var(--q-text-subtle)';

  // Queue readings for the sparkline (the analysis window on past games)
  const spark = queueSeries
    .filter((p) => !range || (p.minute >= range.start && p.minute <= range.end))
    .map((p) => ({ t: toTime(p.minute).getTime(), y: p.queueTime }));
  const markers = [
    gatesOpen != null && { t: toTime(gatesOpen).getTime(), label: 'Gates open' },
    gameStart != null && { t: toTime(gameStart).getTime(), label: 'Game start' },
  ].filter(Boolean);

  return (
    <button type="button" className="gx-gcard" onClick={() => onOpen(stats)}>
      <div className="gx-gcard-head">
        <GateIcon imageURL={gate.imageURL} label={label} className="gx-gcard-icon" />
        <span className="gx-gcard-name">{label}</span>
        <Icon name="chevron-right" className="gx-gcard-chevron" />
      </div>

      <div className="gx-gcard-queue">
        <div className="cx-metric-label">
          {queue && <span className="cx-status-dot" style={{ backgroundColor: color }} />}
          {isLive ? 'Queue now' : 'Peak queue'}
        </div>
        {queue ? (
          <div className="gx-gcard-queue-row">
            <span className="gx-gcard-queue-value" style={{ color }}>
              {queue.value}
              <span className="gx-gcard-unit">min</span>
            </span>
            {!isLive && <span className="gx-gcard-at">at {minuteToClock(queue.minute)}</span>}
          </div>
        ) : (
          <div className="gx-gcard-queue-none">{hasQueue ? 'No recent reading' : 'No queue readings'}</div>
        )}
        {hasQueue && (
          <div className="gx-gcard-spark">
            <Sparkline
              points={spark}
              color="var(--q-text-subtle)"
              valueColor={queueColor}
              height={46}
              minMax={5}
              formatValue={unitFormatter('min')}
              markers={markers}
              markerLabel="none"
            />
          </div>
        )}
      </div>

      <div className="gx-gcard-counts">
        <div>
          <div className="gx-gcard-count-label">Entrances</div>
          <div className="gx-gcard-count">{entrances.toLocaleString()}</div>
          {isLive && (
            <>
              <div className="gx-gcard-rate">{formatRate(entryRate)} / min now</div>
              <RateTrend trend={entryTrend} />
            </>
          )}
        </div>
        <div>
          <div className="gx-gcard-count-label">Exits</div>
          <div className="gx-gcard-count">{exits.toLocaleString()}</div>
          {isLive && (
            <>
              <div className="gx-gcard-rate">{formatRate(exitRate)} / min now</div>
              <RateTrend trend={exitTrend} />
            </>
          )}
        </div>
      </div>

      <div className="gx-gcard-shares">
        <div className="gx-gcard-share">
          <div className="gx-gcard-share-bar">
            <span style={{ width: `${Math.min(100, entryShare)}%` }} />
          </div>
          <span className="gx-gcard-share-text">{Math.round(entryShare)}% of entries</span>
        </div>
        <div className="gx-gcard-share gx-gcard-share-exits">
          <div className="gx-gcard-share-bar">
            <span style={{ width: `${Math.min(100, exitShare)}%` }} />
          </div>
          <span className="gx-gcard-share-text">{Math.round(exitShare)}% of exits</span>
        </div>
      </div>
    </button>
  );
};

export default GateCard;
